import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SalePageMobile.module.css';
import { productService, Product } from '../../services/productService';
import { cartService, CartItem } from '../../services/cartService';
import { customerV1Service, Customer } from '../../services/customerV1Service';
import { orderService } from '../../services/orderService';
import { paymentService } from '../../services/paymentService';
import AddCustomerModalMobile from '../../components/pos/AddCustomeModalMobile';
import { UserOutlined, SearchOutlined, ShoppingCartOutlined, UserAddOutlined, SaveOutlined, LoadingOutlined, CheckCircleOutlined, MinusOutlined, PlusOutlined, PhoneOutlined, ReloadOutlined } from '@ant-design/icons';
import { Modal, Button } from 'antd';

export default function POSScreen() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [subtotal, setSubtotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [total, setTotal] = useState(0);
  const [validating, setValidating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState<'success' | 'error' | 'info'>('info');
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Load products from cache or API
  const loadProducts = async () => {
    try {
      // Kiểm tra cache trước - sử dụng key thống nhất
      const cachedProducts = localStorage.getItem('pos_products_cache');
      const cacheTimestamp = localStorage.getItem('pos_products_cache_timestamp');
      const now = Date.now();
      const cacheAge = now - (parseInt(cacheTimestamp || '0'));
      
      // Cache hợp lệ trong 30 phút
      if (cachedProducts && cacheAge < 30 * 60 * 1000) {
        const parsedProducts = JSON.parse(cachedProducts);
        setProducts(parsedProducts);
        return;
      }
      
      // Nếu không có cache hoặc cache hết hạn, load từ API
      setLoadingProducts(true);
      const res = await productService.getProducts({ pageSize: 'all' });
      
      // Cập nhật state trước
      setProducts(res.data);
      
      // Sau đó mới cache để tránh trùng lặp - sử dụng key thống nhất
      localStorage.setItem('pos_products_cache', JSON.stringify(res.data));
      localStorage.setItem('pos_products_cache_timestamp', now.toString());
      
    } catch (error) {
      console.error('Lỗi load sản phẩm:', error);
      // Fallback: thử load từ cache cũ nếu có
      const cachedProducts = localStorage.getItem('pos_products_cache');
      if (cachedProducts) {
        setProducts(JSON.parse(cachedProducts));
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load products khi component mount
  useEffect(() => {
    // Dọn dẹp cache cũ trước khi load
    cleanupOldCache();
    loadProducts();
  }, []);

  // Dọn dẹp cache cũ để tránh trùng lặp
  const cleanupOldCache = () => {
    try {
      // Xóa cache cũ nếu có
      localStorage.removeItem('cached_products');
      localStorage.removeItem('cached_products_timestamp');
      console.log('Cleaned up old cache keys');
    } catch (error) {
      console.error('Error cleaning up old cache:', error);
    }
  };

  // Tìm kiếm sản phẩm từ cache
  useEffect(() => {
    if (search.trim() === '') {
      setShowDropdown(false);
      return;
    }
    const timeout = setTimeout(() => {
      // Tìm kiếm từ cache thay vì gọi API
      const cachedProducts = localStorage.getItem('pos_products_cache');
      if (cachedProducts) {
        const allProducts = JSON.parse(cachedProducts);
        const filteredProducts = allProducts.filter((p: Product) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.code.toLowerCase().includes(search.toLowerCase())
        );
        setProducts(filteredProducts);
        setShowDropdown(true);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Ẩn dropdown khi click ra ngoài
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Validate cart khi thay đổi
  useEffect(() => {
    if (cart.length === 0) {
      setSubtotal(0);
      setTotal(0);
      return;
    }
    // Bỏ setValidating(true) để không loading khi thêm sản phẩm
    cartService.validateCart(cart).then((res: any) => {
      setSubtotal(res.totalAmount);
      setTotal(res.totalAmount - discount);
    }).catch(() => {
      // Tính tổng tiền thủ công khi offline
      const sum = cart.reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 0), 0);
      setSubtotal(sum);
      setTotal(sum - discount);
    });
  }, [cart, discount]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

  // Thêm sản phẩm vào giỏ
  const addToCart = (product: Product) => {
    setCart(prev => {
      const idx = prev.findIndex(i => i.productId === product.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx].quantity += 1;
        return updated;
      }
      return [...prev, { productId: product.id, quantity: 1, price: product.price }];
    });
  };

  // Cập nhật số lượng
  const updateQuantity = (index: number, change: number) => {
    setCart(prev => {
      const updated = [...prev];
      updated[index].quantity += change;
      if (updated[index].quantity <= 0) updated.splice(index, 1);
      return updated;
    });
  };

  // Tạo khách hàng mới (mở modal)
  const handleCreateCustomer = () => {
    setShowAddCustomerModal(true);
  };

  // Xử lý khi tạo khách hàng thành công
  const handleCustomerCreated = (newCustomer: Customer) => {
    setCustomer(newCustomer);
  };

  // Refresh cache sản phẩm
  const refreshProductsCache = async () => {
    try {
      setLoadingProducts(true);
      const res = await productService.getProducts({ pageSize: 'all' });
      
      // Cập nhật state trước
      setProducts(res.data);
      
      // Sau đó mới cache để tránh trùng lặp
      localStorage.setItem('pos_products_cache', JSON.stringify(res.data));
      localStorage.setItem('pos_products_cache_timestamp', Date.now().toString());
      
      console.log('Cache refreshed successfully:', res.data.length, 'products');
    } catch (error) {
      console.error('Lỗi refresh cache:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Clean tất cả dữ liệu sau khi thanh toán thành công
  const cleanAllData = () => {
    setCustomer(null);
    setCart([]);
    setSubtotal(0);
    setDiscount(0);
    setTotal(0);
    setSearch('');
    setShowDropdown(false);
    console.log('All data cleaned: customer, cart, totals, search');
  };

  // Đóng màn hình bán hàng
  const handleCloseScreen = () => {
    // Kiểm tra nếu có sản phẩm trong giỏ hàng
    if (cart.length > 0) {
      setShowCloseModal(true);
      return;
    }
    // Nếu không có sản phẩm, đóng trực tiếp
    handleConfirmClose();
  };

  // Xác nhận đóng màn hình
  const handleConfirmClose = () => {
    cleanAllData();
    navigate(-1); // Quay về trang trước
  };

  // Lưu đơn & thanh toán
  const handleSaveAndPay = async () => {
    setLoadingPayment(true);
    try {
      let currentCustomer = customer;
      // Nếu không có khách hàng được chọn, luôn sử dụng khách lẻ mặc định
      if (!currentCustomer) {
        try {
          // Lấy khách lẻ mặc định từ server
          currentCustomer = await customerV1Service.getDefaultCustomer();
        } catch (err) {
          // Nếu không kết nối được server, sử dụng khách lẻ mặc định offline
          currentCustomer = {
            id: '0',
            fullName: 'Khách lẻ',
            phoneNumber: '0000000000',
            address: '',
            code: 'CUS-KHACHLE',
            createdAt: new Date().toISOString(),
          };
        }
      }
      
      if (cart.length === 0) {
        alert('Chưa có sản phẩm trong giỏ!');
        return;
      }
      
      // Validate cart items
      const invalidItems = cart.filter(item => !item.productId || !item.quantity || item.quantity <= 0);
      if (invalidItems.length > 0) {
        alert('Có sản phẩm không hợp lệ trong giỏ hàng!');
        return;
      }
      
      console.log('Creating order with customer:', currentCustomer);
      console.log('Cart items:', cart);
      console.log('Order totals:', { subtotal, discount, total });
      
      // Tạo hóa đơn
      let invoice;
      try {
        // Đảm bảo customer_id là số hợp lệ
        const customerId = currentCustomer.id === '0' ? 0 : parseInt(currentCustomer.id);
        console.log('Using customer ID:', customerId);
        
        invoice = await orderService.createInvoice({
          customerId: customerId.toString(),
          items: cart,
          subtotal,
          discount,
          tax: 0,
          totalAmount: total,
        });
        console.log('Order created successfully:', invoice);
      } catch (orderError) {
        console.error('Error creating order:', orderError);
        throw new Error('Lỗi tạo đơn hàng: ' + (orderError as any).message);
      }
      
      // Tạo payment
      try {
        console.log('Creating payment for order:', invoice.id);
        await paymentService.createPayment({
          orderId: invoice.id as number,
          amount: total,
          method: 'Tiền mặt',
        });
        console.log('Payment created successfully');
      } catch (paymentError) {
        console.error('Error creating payment:', paymentError);
        throw new Error('Lỗi tạo thanh toán: ' + (paymentError as any).message);
      }
      
      // Refresh cache sau khi bán hàng thành công
      try {
        await refreshProductsCache();
        console.log('Cache refreshed successfully');
      } catch (cacheError) {
        console.error('Error refreshing cache:', cacheError);
        // Không throw error vì refresh cache không quan trọng
      }
      
      // Clean thông tin khách hàng và giỏ hàng sau khi thanh toán thành công
      cleanAllData();
      setModalOpen(true);
      console.log('Customer info and cart cleared after successful payment');
    } catch (error) {
      console.error('Lỗi thanh toán:', error);
      
      // Debug: Log chi tiết lỗi
      console.log('Error details:', {
        error: error,
        errorType: typeof error,
        errorMessage: error instanceof Error ? error.message : 'Not Error instance',
        response: (error as any).response?.data,
        status: (error as any).response?.status
      });
      
      // Lấy thông báo lỗi chính xác từ API
      let errorMessage = 'Có lỗi xảy ra khi thanh toán! Vui lòng thử lại.';
      
      if (error instanceof Error) {
        // Kiểm tra nếu có response từ API
        const errorResponse = (error as any).response?.data;
        if (errorResponse) {
          // Ưu tiên message từ API
          if (errorResponse.message) {
            errorMessage = errorResponse.message;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } else {
          // Sử dụng message từ Error object
          errorMessage = error.message;
        }
      } else if (typeof error === 'object' && error !== null) {
        // Kiểm tra nếu error là object có chứa message
        const errorObj = error as any;
        if (errorObj.message) {
          errorMessage = errorObj.message;
        } else if (errorObj.error) {
          errorMessage = errorObj.error;
        }
      }
      
      // Format thông báo lỗi để hiển thị đẹp hơn
      if (errorMessage.includes('Không đủ tồn kho')) {
        // Thêm icon và format cho thông báo lỗi tồn kho
        errorMessage = `⚠️ ${errorMessage}`;
      } else if (errorMessage.includes('Insufficient Stock')) {
        // Thêm icon cho thông báo lỗi tiếng Anh
        errorMessage = `⚠️ ${errorMessage}`;
      }
      
      console.log('Final error message:', errorMessage);
      setModalMessage(errorMessage);
      setModalType('error');
      setShowModal(true);
    } finally {
      setLoadingPayment(false);
    }
  };

  return (
    <div className={styles['pos-mobile-container']}>
      {/* Top header bar */}
      <div className={styles['pos-mobile-top-bar']}>
        <span className={styles['pos-mobile-close-btn']} onClick={handleCloseScreen}>✕</span>
        <span className={styles['pos-mobile-title']}>Bán hàng</span>
        <span className={styles['pos-mobile-settings-btn']}></span>
      </div>

      <main className={styles['pos-mobile-cart-section']}>
        <div className={styles['pos-mobile-search']} ref={searchRef}>
          <div className={styles['pos-mobile-search-row']}>
            <div className={styles['pos-mobile-search-input-wrapper']}>
              <input
                type="text"
                placeholder="Tên, mã hàng, mã vạch, lo..."
                className={styles['pos-mobile-search-input']}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => { if (products.length > 0) setShowDropdown(true); }}
              />
              <SearchOutlined className={styles['pos-mobile-search-icon']} />
            </div>
            <button className={styles['pos-mobile-customer-add-btn']} onClick={handleCreateCustomer}>
              <UserAddOutlined />
            </button>
            <button 
              className={styles['pos-mobile-refresh-btn']} 
              onClick={refreshProductsCache}
              title="Làm mới danh sách sản phẩm"
              disabled={loadingProducts}
            >
              {loadingProducts ? (
                <LoadingOutlined style={{ fontSize: '18px' }} />
              ) : (
                <ReloadOutlined style={{ fontSize: '18px' }} />
              )}
            </button>
          </div>
          {/* Gợi ý sản phẩm */}
          {products.length > 0 && showDropdown && (
            <div className={styles['pos-mobile-search-dropdown']}>
              {products.map(p => (
                <div
                  key={p.id}
                  className={styles['pos-mobile-search-item']}
                  onClick={() => { addToCart(p); setShowDropdown(false); }}
                >
                  <b>{p.name}</b> <span style={{ color: '#888' }}>({p.code})</span> - {formatCurrency(p.price)}
                </div>
              ))}
            </div>
          )}
        </div>

        <h2 className={styles['pos-mobile-cart-title']}>Đơn hàng hiện tại</h2>
        <div className={styles['pos-mobile-cart-list']}>
          {cart.length === 0 ? (
            <div className={styles['pos-mobile-cart-empty']}>
              <ShoppingCartOutlined style={{ fontSize: '2rem', color: '#d1d5db' }} />
              <p style={{ marginTop: '0.5rem' }}>Chưa có sản phẩm nào trong giỏ</p>
            </div>
          ) : (
            cart.map((item, index) => {
              const prod = products.find(p => p.id === item.productId);
              return (
                <div key={`${item.productId}-${index}`} className={styles['pos-mobile-cart-card']}>
                  <div className={styles['pos-mobile-cart-info']}>
                    <p className={styles['pos-mobile-cart-name']} title={prod?.name || item.productId.toString()}>
                      {prod?.name || item.productId.toString()}
                    </p>
                    <div className={styles['pos-mobile-cart-meta']}>
                      <span className={styles['pos-mobile-cart-unit']}>Lô</span>
                      <span className={styles['pos-mobile-cart-code']}>{prod?.code || 'N/A'}</span>
                    </div>
                  </div>
                  <div className={styles['pos-mobile-cart-right']}>
                    <div className={styles['pos-mobile-cart-price-display']}>
                      {formatCurrency(item.price)}
                    </div>
                    <div className={styles['pos-mobile-cart-qty']}>
                      <button 
                        onClick={() => updateQuantity(index, -1)} 
                        className={styles['pos-mobile-cart-qty-btn']} 
                        aria-label="Giảm số lượng"
                      >
                        <MinusOutlined style={{ fontSize: '14px' }} />
                      </button>
                      <span className={styles['pos-mobile-cart-qty-value']}>{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(index, 1)} 
                        className={styles['pos-mobile-cart-qty-btn']} 
                        aria-label="Tăng số lượng"
                      >
                        <PlusOutlined style={{ fontSize: '14px' }} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>
      <footer className={styles['pos-mobile-footer']}>
        <div>
          <div className={styles['pos-mobile-summary-header']}>
            <span>Tổng tiền hàng</span>
            <div className={styles['pos-mobile-item-count']}>
              <span className={styles['pos-mobile-item-count-number']}>{cart.length}</span>
            </div>
            <span className={styles['pos-mobile-total-amount']}>{formatCurrency(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className={styles['pos-mobile-summary-row']}>
              <span>Giảm giá:</span>
              <span style={{ fontWeight: 600, color: '#ef4444' }}>{formatCurrency(discount)}</span>
            </div>
          )}
        </div>
        <div className={styles['pos-mobile-footer-btns']}>
          <button className={styles['pos-mobile-btn-draft']}>
            Lưu tạm
          </button>
          <button className={styles['pos-mobile-btn-save']} onClick={handleSaveAndPay} disabled={cart.length === 0 || loadingPayment}>
            {loadingPayment ? (
              <>
                <LoadingOutlined style={{ marginRight: '0.5rem' }} />Đang xử lý...
              </>
            ) : (
              'Thanh toán'
            )}
          </button>
        </div>
      </footer>
      {modalOpen && (
        <div className={styles['pos-mobile-modal']}>
          <div className={styles['pos-mobile-modal-content']}>
            <div className={styles['pos-mobile-modal-icon']}>
              <CheckCircleOutlined />
            </div>
            <h3 className={styles['pos-mobile-modal-title']}>Thanh toán thành công!</h3>
            <p className={styles['pos-mobile-modal-desc']}>Đã tạo hóa đơn mới.</p>
            <button
              onClick={() => {
                setModalOpen(false);
                cleanAllData(); // Clean tất cả dữ liệu khi đóng modal
                console.log('All data cleared when closing success modal');
              }}
              className={styles['pos-mobile-modal-close']}
            >
              Đóng
            </button>
          </div>
        </div>
      )}

      {/* Modal thêm khách hàng */}
      <AddCustomerModalMobile
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onSuccess={handleCustomerCreated}
      />
      
      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={<Button type="primary" onClick={() => setShowModal(false)}>Đóng</Button>}
        centered
        title={modalType === 'success' ? 'Thành công' : modalType === 'error' ? 'Lỗi' : 'Thông báo'}
        className={modalType === 'error' ? styles['pos-mobile-modal-error'] : ''}
      >
        <div className={modalType === 'error' ? styles['pos-mobile-modal-error-message'] : ''}>
          {modalMessage}
        </div>
      </Modal>

      {/* Modal xác nhận đóng màn hình */}
      <Modal
        open={showCloseModal}
        onCancel={() => setShowCloseModal(false)}
        onOk={handleConfirmClose}
        okText="Đồng ý"
        cancelText="Hủy"
        centered
        title="Xác nhận đóng màn hình"
        className={styles['pos-mobile-modal-confirm-close']}
      >
        <p>Bạn có chắc muốn đóng màn hình bán hàng? Dữ liệu giỏ hàng sẽ bị mất.</p>
      </Modal>
    </div>
  );
}
